import styled, { css } from "styled-components";

import {
  COLORS,
  THEME_FALLBACKS,
  GREEN_CSS_VARS,
} from "../../common/colors";

// ═══════════════════════════════════════════════════════════════════════════════
// YOG GOALS — DESIGN TOKENS
// Same visual language as the existing Yog section.
// ═══════════════════════════════════════════════════════════════════════════════

const kFadeUp = css`
  @media (prefers-reduced-motion: no-preference) {
    animation: yogGoalsFadeUp 0.55s ease both;
  }

  @keyframes yogGoalsFadeUp {
    from {
      opacity: 0;
      transform: translateY(18px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const kScaleIn = css`
  @media (prefers-reduced-motion: no-preference) {
    animation: yogGoalsScaleIn 0.42s ease both;
  }

  @keyframes yogGoalsScaleIn {
    from {
      opacity: 0;
      transform: scale(0.985);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════════════════════════

export const Section = styled.section`
  ${GREEN_CSS_VARS}

  --c-bg-from: #faf7f2;
  --c-bg-to: #f2ebe0;

  --c-text: ${({ theme }) =>
    theme.TextColorBlack ?? THEME_FALLBACKS.TextColorBlack};

  --r-sm: 0.65rem;
  --r: 1rem;
  --r-lg: 1.4rem;
  --r-xl: 2rem;

  --s-xs: 0 1px 4px rgba(0, 0, 0, 0.06);
  --s-sm: 0 2px 12px rgba(0, 0, 0, 0.07);
  --s: 0 4px 24px rgba(0, 0, 0, 0.09);
  --s-lg: 0 8px 40px rgba(0, 0, 0, 0.13);

  --sp-2: 0.5rem;
  --sp-3: 0.75rem;
  --sp-4: 1rem;
  --sp-5: 1.25rem;
  --sp-6: 1.5rem;
  --sp-8: 2rem;
  --sp-10: 2.5rem;
  --sp-12: 3rem;

  --t-fast: 0.15s ease;
  --t: 0.22s ease;

  position: relative;

  background: linear-gradient(
    170deg,
    var(--c-bg-from) 0%,
    var(--c-bg-to) 100%
  );
`;

// ═══════════════════════════════════════════════════════════════════════════════
// BACKGROUND BLOBS
// ═══════════════════════════════════════════════════════════════════════════════

export const Blob = styled.span`
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;

  &.g1 {
    width: 460px;
    height: 460px;

    background: radial-gradient(
      circle,
      ${COLORS.primary} 0%,
      transparent 70%
    );

    opacity: 0.075;
    top: -140px;
    left: -100px;
    filter: blur(65px);
  }

  &.g2 {
    width: 330px;
    height: 330px;

    background: radial-gradient(
      circle,
      ${COLORS.primaryLight} 0%,
      transparent 70%
    );

    opacity: 0.075;
    bottom: 80px;
    right: -80px;
    filter: blur(60px);
  }

  &.g3 {
    width: 220px;
    height: 220px;

    background: radial-gradient(
      circle,
      ${COLORS.accent} 0%,
      transparent 70%
    );

    opacity: 0.055;
    top: 42%;
    left: 42%;
    filter: blur(50px);
  }

  @media (max-width: 48em) {
    &.g1 {
      width: 240px;
      height: 240px;
    }

    &.g2 {
      width: 180px;
      height: 180px;
    }

    &.g3 {
      display: none;
    }
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// INNER
// ═══════════════════════════════════════════════════════════════════════════════

export const Inner = styled.div`
  position: relative;
  z-index: 1;

  max-width: 1240px;
  margin: 0 auto;

  padding: var(--sp-8) var(--sp-4) var(--sp-12);
  box-sizing: border-box;

  @media (min-width: 48em) {
    padding: var(--sp-10) 6% var(--sp-12);
  }

  @media (min-width: 64em) {
    padding: var(--sp-12) 7% 5rem;
  }

  @media (min-width: 80em) {
    padding: var(--sp-12) 2% 5rem;
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// HEADER
// ═══════════════════════════════════════════════════════════════════════════════

export const PageHeader = styled.header`
  text-align: center;

  margin-bottom: var(--sp-8);

  ${kFadeUp};

  &::after {
    content: "";

    display: block;

    width: 56px;
    height: 4px;

    border-radius: 999px;

    background: linear-gradient(
      90deg,
      #3d6b4f 0%,
      #7a9e4e 50%,
      #c9a84c 100%
    );

    margin: var(--sp-4) auto 0;
  }

  @media (min-width: 48em) {
    margin-bottom: var(--sp-10);
  }
`;

export const Eyebrow = styled.span`
  display: block;

  font-size: 0.68rem;
  font-weight: 700;

  letter-spacing: 0.2em;
  text-transform: uppercase;

  color: #3d6b4f;

  margin-bottom: var(--sp-3);
`;

export const Title = styled.h2`
  font-size: clamp(2rem, 6vw, 3.2rem);

  font-weight: 800;

  color: #2e1f0f;

  margin: 0;

  line-height: 1.1;

  letter-spacing: -0.025em;
`;

export const Desc = styled.p`
  font-size: clamp(0.85rem, 2.2vw, 0.95rem);

  color: var(--c-text);

  width: 100%;
  max-width: 680px;

  margin: 0 auto;

  padding: 0 var(--sp-4);

  box-sizing: border-box;

  line-height: 1.85;

  opacity: 0.72;

  @media (min-width: 48em) {
    padding: 0;
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// GOALS GRID
// ═══════════════════════════════════════════════════════════════════════════════

export const GoalsGrid = styled.div`
  display: grid;

  grid-template-columns: 1fr;

  gap: var(--sp-3);

  @media (min-width: 36em) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    gap: var(--sp-4);
  }

  @media (min-width: 64em) {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    gap: var(--sp-5);
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// GOAL CARD
// ═══════════════════════════════════════════════════════════════════════════════

export const GoalCard = styled.div`
  appearance: none;

  width: 100%;

  box-sizing: border-box;

  display: flex;

  align-items: flex-start;

  gap: var(--sp-4);

  position: relative;

  overflow: hidden;

  padding: var(--sp-5);

  min-height: 170px;

  text-align: left;

  border: 1px solid var(--c-border);

  border-radius: var(--r-lg);

  background: var(--c-surface);

  color: var(--c-text);

  box-shadow: var(--s-sm);

  backdrop-filter: blur(12px);

  cursor: ${({ type }) =>
    type === "button" ? "pointer" : "default"};

  transition:
    transform var(--t),
    box-shadow var(--t),
    border-color var(--t),
    background var(--t);

  ${({ $index }) => `
    ${kScaleIn}

    animation-delay: ${Math.min($index * 55, 275)}ms;
  `}

  &::after {
    content: "";

    position: absolute;

    width: 110px;
    height: 110px;

    border-radius: 50%;

    right: -55px;
    bottom: -55px;

    background: radial-gradient(
      circle,
      ${COLORS.primary} 0%,
      transparent 70%
    );

    opacity: 0.07;

    pointer-events: none;

    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }

  &:hover {
    border-color: var(--c-border-accent);

    box-shadow: var(--s-lg);

    transform: translateY(-4px);

    background: var(--c-surface-high);

    &::after {
      transform: scale(1.25);

      opacity: 0.11;
    }
  }

  &:active {
    transform: translateY(-1px) scale(0.99);
  }

  &:focus-visible {
    outline: 2px solid var(--c-accent);

    outline-offset: 3px;
  }

  @media (min-width: 48em) {
    padding: var(--sp-6);

    min-height: 185px;
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// ICON
// ═══════════════════════════════════════════════════════════════════════════════

export const GoalIcon = styled.span`
  flex: 0 0 48px;

  width: 48px;
  height: 48px;

  display: grid;

  place-items: center;

  border-radius: 50%;

  background: var(--c-accent-tint);

  border: 1px solid var(--c-border-accent);

  font-size: 1.35rem;

  box-shadow: var(--s-xs);
`;

// ═══════════════════════════════════════════════════════════════════════════════
// CONTENT
// ═══════════════════════════════════════════════════════════════════════════════

export const GoalContent = styled.div`
  flex: 1;

  min-width: 0;

  padding-right: 1.25rem;
`;

export const GoalTitle = styled.h3`
  margin: 0 0 0.5rem;

  color: #2e1f0f;

  font-size: 1rem;

  font-weight: 800;

  line-height: 1.3;
`;

export const GoalDescription = styled.p`
  margin: 0;

  color: var(--c-text);

  font-size: 0.8rem;

  line-height: 1.75;

  opacity: 0.7;
`;

// ═══════════════════════════════════════════════════════════════════════════════
// ARROW
// ═══════════════════════════════════════════════════════════════════════════════

export const GoalArrow = styled.span`
  position: absolute;

  right: var(--sp-5);
  top: var(--sp-5);

  width: 1.8rem;
  height: 1.8rem;

  display: grid;

  place-items: center;

  border-radius: 50%;

  color: var(--c-accent);

  background: var(--c-accent-tint);

  font-size: 1rem;

  font-weight: 700;

  transition:
    transform var(--t),
    background var(--t),
    color var(--t);

  ${GoalCard}:hover & {
    transform: translateX(3px);

    background: var(--c-accent);

    color: #fff;
  }

  @media (max-width: 35.99em) {
    right: var(--sp-4);

    top: var(--sp-4);
  }
`;

// ═══════════════════════════════════════════════════════════════════════════════
// TRIAL CTA
// ═══════════════════════════════════════════════════════════════════════════════

export const TrialRow = styled.div`
  margin-top: var(--sp-8);

  padding: var(--sp-5);

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: var(--sp-5);

  border: 1px solid var(--c-border-accent);

  border-radius: var(--r-lg);

  background: linear-gradient(
    135deg,
    rgba(61, 107, 79, 0.07),
    rgba(201, 168, 76, 0.08)
  );

  box-shadow: var(--s-sm);

  ${kFadeUp};

  @media (min-width: 48em) {
    padding: var(--sp-6) var(--sp-8);

    margin-top: var(--sp-10);
  }

  @media (max-width: 35.99em) {
    flex-direction: column;

    align-items: stretch;

    text-align: center;
  }
`;

export const TrialText = styled.div`
  min-width: 0;
`;

export const TrialTitle = styled.h3`
  margin: 0 0 0.3rem;

  color: #2e1f0f;

  font-size: 1rem;

  font-weight: 800;
`;

export const TrialDescription = styled.p`
  margin: 0;

  color: var(--c-text);

  font-size: 0.8rem;

  line-height: 1.6;

  opacity: 0.68;
`;

export const TrialButton = styled.a`
  flex-shrink: 0;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  min-height: 46px;

  padding: 0.7rem 1.35rem;

  border-radius: 999px;

  border: 1.5px solid var(--c-accent);

  background: linear-gradient(
    135deg,
    var(--c-accent),
    var(--c-accent-2)
  );

  color: #fff;

  font-size: 0.82rem;

  font-weight: 800;

  text-decoration: none;

  box-shadow: 0 5px 16px ${COLORS.primaryGlow};

  transition:
    transform var(--t-fast),
    box-shadow var(--t),
    filter var(--t);

  &:hover {
    transform: translateY(-2px);

    box-shadow: 0 8px 22px ${COLORS.primaryGlow};

    filter: brightness(1.03);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid var(--c-accent);

    outline-offset: 3px;
  }
`;